import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateIT } from "@/lib/date";
import { toast } from "sonner";
import { X, Repeat, ArrowUp, ArrowDown } from "lucide-react";

const STATUS_LABELS = {
  scheduled: { l: "Programmata", c: "text-white/70 bg-white/5" },
  completed: { l: "Effettuata", c: "text-[#10B981] bg-[#10B981]/10" },
  rescheduled: { l: "Spostata", c: "text-[#F59E0B] bg-[#F59E0B]/10" },
  cancelled: { l: "Annullata", c: "text-[#FF3366] bg-[#FF3366]/10" },
  persa: { l: "Persa", c: "text-[#F97316] bg-[#F97316]/15" },
};
const CANCELLED_BY = { admin: "Trainer", client: "Cliente" };

const FILTERS = [
  { k: "all", l: "Tutte" },
  { k: "scheduled", l: "Programmate" },
  { k: "completed", l: "Effettuate" },
  { k: "rescheduled", l: "Spostate" },
  { k: "cancelled", l: "Annullate" },
  { k: "persa", l: "Perse" },
];

export default function LessonsHistoryPage({ role, clients = [] }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // desc = newest first
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    try {
      let url = "/history" + (filter !== "all" ? `?status=${filter}` : "");
      if (role === "admin" && clientFilter) url += (url.includes("?") ? "&" : "?") + `client_id=${clientFilter}`;
      const { data } = await api.get(url);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, clientFilter]);

  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const k1 = `${a.date} ${a.time}`;
      const k2 = `${b.date} ${b.time}`;
      return sortDir === "asc" ? k1.localeCompare(k2) : k2.localeCompare(k1);
    });
    return arr;
  }, [items, sortDir]);

  const toggleSort = () => setSortDir(d => d === "asc" ? "desc" : "asc");

  const changeStatus = async (b, status) => {
    let reason = "";
    if (status === "cancelled" || status === "rescheduled") {
      reason = window.prompt(`Motivo per ${status === "cancelled" ? "l'annullamento" : "lo spostamento"} (opzionale):`, "") || "";
      if (reason === null) return;
    }
    try {
      await api.patch(`/bookings/${b.id}/status`, { status, reason });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelSelf = async (b) => {
    if (!window.confirm(`Annullare la lezione del ${formatDateIT(b.date)} alle ${b.time}?`)) return;
    try { await api.delete(`/bookings/${b.id}`); toast.success("Lezione annullata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const repeat = async (b) => {
    const raw = window.prompt("Quante settimane vuoi ripetere questa prenotazione (1-52)?", "4");
    const n = parseInt(raw || "0");
    if (!n || n < 1 || n > 52) return;
    try {
      const r = await api.post(`/bookings/${b.id}/repeat`, { occurrences: n, frequency: "weekly" });
      const created = (r.data.created || []).length;
      const skipped = r.data.skipped || [];
      let msg = `Create ${created} prenotazioni ripetute.`;
      if (skipped.length) msg += ` Saltate ${skipped.length}: ${skipped.map(x => `${formatDateIT(x.date)} (${x.reason})`).join(", ")}`;
      toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="label-caps">Storico lezioni</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mt-1 mb-6">{items.length} lezioni</h2>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex border border-white/10 flex-wrap">
          {FILTERS.map(f => (
            <button key={f.k} data-testid={`history-filter-${f.k}`} onClick={() => setFilter(f.k)}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-xs transition-colors border-l first:border-l-0 border-white/10 ${filter === f.k ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}>
              {f.l}
            </button>
          ))}
        </div>
        {role === "admin" && (
          <select data-testid="history-client-filter" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none">
            <option value="" className="bg-[#120B19]">Tutti i clienti</option>
            {clients.map(c => <option key={c.id} value={c.id} className="bg-[#120B19]">{c.display_name || c.name}</option>)}
          </select>
        )}
      </div>

      <div className="card-surface table-scroll">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-white/10">
            <tr>
              <th className="label-caps text-left p-4">
                <button
                  data-testid="history-sort-date"
                  onClick={toggleSort}
                  className="flex items-center gap-1 hover:text-white uppercase tracking-widest text-[10px]"
                  title={`Ordina ${sortDir === "asc" ? "dal più recente" : "dal più vecchio"}`}
                >
                  Data {sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </button>
              </th>
              {["Ora", "Servizio", ...(role === "admin" ? ["Cliente"] : []), "Stato", "Annullata da", "Azioni"].map(h => (
                <th key={h} className="label-caps text-left p-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(b => {
              const st = STATUS_LABELS[b.status] || STATUS_LABELS.scheduled;
              const isFuture = b.date >= today && b.status === "scheduled";
              return (
                <tr key={b.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 text-sm">{formatDateIT(b.date)}</td>
                  <td className="p-4 text-sm">{b.time}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest svc-${b.service_type}`}>
                      {b.service_label}
                    </span>
                  </td>
                  {role === "admin" && <td className="p-4 text-sm">{b.client_name}</td>}
                  <td className="p-4">
                    <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${st.c}`}>
                      {st.l}
                    </span>
                  </td>
                  <td className="p-4 text-white/60 text-xs">{b.status === "cancelled" ? (CANCELLED_BY[b.cancelled_by] || "—") : "—"}</td>
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {isFuture && (
                        <>
                          <button onClick={() => repeat(b)} data-testid={`history-repeat-${b.id}`}
                            className="text-[#A855F7]/80 hover:text-[#A855F7] text-xs flex items-center gap-1">
                            <Repeat size={12} /> Ripeti
                          </button>
                          <button onClick={() => cancelSelf(b)} data-testid={`history-cancel-${b.id}`}
                            className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs flex items-center gap-1">
                            <X size={12} /> Annulla
                          </button>
                        </>
                      )}
                      {role === "admin" && (
                        <select value={b.status} onChange={e => changeStatus(b, e.target.value)}
                          data-testid={`history-status-${b.id}`}
                          className="bg-transparent border border-white/15 p-1 text-white text-xs focus:border-[#A855F7] outline-none">
                          <option value="scheduled" className="bg-[#120B19]">Programmata</option>
                          <option value="completed" className="bg-[#120B19]">Effettuata</option>
                          <option value="rescheduled" className="bg-[#120B19]">Spostata</option>
                          <option value="cancelled" className="bg-[#120B19]">Annullata</option>
                          <option value="persa" className="bg-[#120B19]">Persa</option>
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sortedItems.length === 0 && <tr><td colSpan={role === "admin" ? 7 : 6} className="p-8 text-center text-white/40">Nessuna lezione nello storico.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
